import type { FaceLandmarker as FaceLandmarkerType } from "@mediapipe/tasks-vision";

import { authService } from "@/services/auth-service";

// On-device face-landmark detection (MediaPipe Tasks Vision, WASM) used to tell
// when the live camera framing matches a reference photo for before/after
// alignment. Runs entirely in the browser; assets are self-hosted so no CDN /
// CSP issues (mirrors the zxing-wasm barcode setup).

// Alignment tolerances, all on normalized [0,1] frame coordinates / degrees.
export const ALIGN_POS_TOL = 0.05; // mid-eye point within 5% of the frame
export const ALIGN_SCALE_TOL = 0.1; // inter-ocular distance within 10%
export const ALIGN_ANGLE_TOL = 6; // eye-line tilt within 6 degrees

export interface FacePose {
  x: number; // mid-eye point (normalized)
  y: number;
  iod: number; // inter-ocular distance (normalized) — a scale/distance proxy
  angle: number; // eye-line angle in degrees — a roll/tilt proxy
}

export interface AlignResult {
  aligned: boolean;
  hint: string | null;
}

// FaceMesh eye-corner indices: [outer, inner].
const LEFT_EYE = [33, 133];
const RIGHT_EYE = [362, 263];

let landmarkerPromise: Promise<FaceLandmarkerType | null> | null = null;

// Detection runs synchronously on the CPU, and its cost grows with the pixels
// it is handed, not with what the model needs: a 12MP chart photo or a
// full-resolution camera frame takes several times longer than a scaled copy
// and locks up an iPad while it runs. Landmarks come back as fractions of the
// image, so detecting on a proportional copy gives the same positions.
const DETECT_MAX_EDGE = 1024;
let detectCanvas: HTMLCanvasElement | null = null;

export function detectionSource(
  source: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement,
  maxEdge = DETECT_MAX_EDGE,
): HTMLImageElement | HTMLVideoElement | HTMLCanvasElement {
  const width =
    source instanceof HTMLVideoElement
      ? source.videoWidth
      : source instanceof HTMLImageElement
        ? source.naturalWidth
        : source.width;
  const height =
    source instanceof HTMLVideoElement
      ? source.videoHeight
      : source instanceof HTMLImageElement
        ? source.naturalHeight
        : source.height;
  const scale = maxEdge / Math.max(width, height);
  if (!width || !height || scale >= 1) return source;
  detectCanvas ??= document.createElement("canvas");
  detectCanvas.width = Math.round(width * scale);
  detectCanvas.height = Math.round(height * scale);
  const context = detectCanvas.getContext("2d");
  if (!context) return source;
  context.drawImage(source, 0, 0, detectCanvas.width, detectCanvas.height);
  return detectCanvas;
}

// Lazily load the WASM runtime + model on first use. Returns null (and caches
// the null-producing promise) if the browser can't run it, so callers degrade
// to the plain ghost overlay instead of throwing.
export function getFaceLandmarker(): Promise<FaceLandmarkerType | null> {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      try {
        const { FaceLandmarker, FilesetResolver } =
          await import("@mediapipe/tasks-vision");
        const fileset = await FilesetResolver.forVisionTasks("/mediapipe/wasm");
        return await FaceLandmarker.createFromOptions(fileset, {
          baseOptions: {
            modelAssetPath: "/models/face_landmarker.task",
            delegate: "CPU",
          },
          runningMode: "IMAGE",
          numFaces: 1,
        });
      } catch (err) {
        console.error("Face alignment unavailable:", err);
        return null;
      }
    })();
  }
  return landmarkerPromise;
}

// Load a reference image the face detector can read pixels from. Local blob:/
// data: URLs (uploads) load directly; remote URLs (gallery signed URLs) go
// through the same-origin image proxy so the detector isn't blocked by CORS.
export async function loadDetectableImage(
  url: string,
): Promise<HTMLImageElement> {
  const isLocal = url.startsWith("blob:") || url.startsWith("data:");
  let src = url;
  let createdBlobUrl: string | null = null;

  if (!isLocal) {
    const token = await authService.getToken();
    const res = await fetch(`/api/proxy-image?url=${encodeURIComponent(url)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`proxy-image failed: ${res.status}`);
    const blob = await res.blob();
    createdBlobUrl = URL.createObjectURL(blob);
    src = createdBlobUrl;
  }

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
      resolve(img);
    };
    img.onerror = () => {
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
      reject(new Error("Failed to load reference image"));
    };
    img.src = src;
  });
}

// Derive the eye-based pose from a MediaPipe landmark list. `mirror` flips x so
// the pose is expressed in the same (possibly mirrored) space the user sees on
// screen, matching the ghost overlay's transform.
export function poseFromLandmarks(
  landmarks: { x: number; y: number }[] | undefined,
  mirror: boolean,
): FacePose | null {
  if (!landmarks || landmarks.length < 468) return null;
  const pt = (i: number) => {
    const p = landmarks[i];
    return { x: mirror ? 1 - p.x : p.x, y: p.y };
  };
  const l0 = pt(LEFT_EYE[0]);
  const l1 = pt(LEFT_EYE[1]);
  const r0 = pt(RIGHT_EYE[0]);
  const r1 = pt(RIGHT_EYE[1]);
  const leftEye = { x: (l0.x + l1.x) / 2, y: (l0.y + l1.y) / 2 };
  const rightEye = { x: (r0.x + r1.x) / 2, y: (r0.y + r1.y) / 2 };
  const dx = rightEye.x - leftEye.x;
  const dy = rightEye.y - leftEye.y;
  return {
    x: (leftEye.x + rightEye.x) / 2,
    y: (leftEye.y + rightEye.y) / 2,
    iod: Math.hypot(dx, dy),
    angle: (Math.atan2(dy, dx) * 180) / Math.PI,
  };
}

// Detect the pose from an image or video frame. Returns null if no face is
// found or the source can't be read (e.g. a cross-origin-tainted image).
export function detectPose(
  landmarker: FaceLandmarkerType,
  source: HTMLImageElement | HTMLVideoElement,
  mirror: boolean,
): FacePose | null {
  try {
    const result = landmarker.detect(detectionSource(source));
    return poseFromLandmarks(result.faceLandmarks?.[0], mirror);
  } catch {
    return null;
  }
}

// Compare a live pose to the reference pose. When not aligned, return the most
// significant single nudge. Poses are in on-screen (mirror-applied) space, and
// left/right is worded for the patient, who faces the camera. In a mirrored
// (front-camera) preview the screen behaves like a mirror, so a face sitting
// right of the target (dx>0) should "Move left"; through the rear camera the
// screen's right is the patient's right, so the same offset means "Move right".
// dy>0 means it sits low → "Move up".
export function comparePoses(
  live: FacePose,
  ref: FacePose,
  mirrored: boolean,
): AlignResult {
  const dx = live.x - ref.x;
  const dy = live.y - ref.y;
  const pos = Math.hypot(dx, dy);
  const scale = ref.iod > 0 ? (live.iod - ref.iod) / ref.iod : 0;
  let angle = live.angle - ref.angle;
  while (angle > 180) angle -= 360;
  while (angle < -180) angle += 360;

  const posOk = pos < ALIGN_POS_TOL;
  const scaleOk = Math.abs(scale) < ALIGN_SCALE_TOL;
  const angleOk = Math.abs(angle) < ALIGN_ANGLE_TOL;
  if (posOk && scaleOk && angleOk) return { aligned: true, hint: null };

  // Pick whichever correction is furthest past its tolerance.
  const posMag = pos / ALIGN_POS_TOL;
  const scaleMag = Math.abs(scale) / ALIGN_SCALE_TOL;
  const angleMag = Math.abs(angle) / ALIGN_ANGLE_TOL;

  if (!scaleOk && scaleMag >= posMag && scaleMag >= angleMag) {
    return { aligned: false, hint: scale < 0 ? "Move closer" : "Move back" };
  }
  if (!posOk && posMag >= angleMag) {
    if (Math.abs(dx) > Math.abs(dy)) {
      const sitsRight = dx > 0;
      return {
        aligned: false,
        hint: sitsRight === mirrored ? "Move left" : "Move right",
      };
    }
    return { aligned: false, hint: dy > 0 ? "Move up" : "Move down" };
  }
  return { aligned: false, hint: "Straighten" };
}

// Face-guide geometry, in the overlay SVG's user units. The overlay draws from
// these and alignment targets them, so the outline and the "Aligned" check can
// never drift apart.
//
// The viewBox sets how big the head sits in the frame. It was fitted to the
// median of ~285 faces in clinics' own camera photos: eyes 40% down the frame,
// eye spacing ~9.7% of the width in 4:3 landscape and ~17.3% in 3:4 portrait.
// Both are the same height-based framing, so one viewBox fits both, and new
// "after" photos line up with the "before" photos clinics already have.
export const FACE_GUIDE_VIEWBOX = { x: -5, y: -19.5, width: 110, height: 147 };
export const FACE_GUIDE_EYES = {
  left: { x: 40.5, y: 39.5 },
  right: { x: 59.5, y: 39.5 },
};

// The face guide's eye targets as a pose in the camera pane's on-screen space
// (x/y as fractions of the pane, iod as a fraction of its width, angle in
// degrees), replaying the SVG's viewBox + "xMidYMid meet" layout.
export function faceGuideTargetPose(
  paneWidth: number,
  paneHeight: number,
): FacePose {
  const vb = FACE_GUIDE_VIEWBOX;
  const scale = Math.min(paneWidth / vb.width, paneHeight / vb.height);
  const offsetX = (paneWidth - vb.width * scale) / 2 - vb.x * scale;
  const offsetY = (paneHeight - vb.height * scale) / 2 - vb.y * scale;
  const { left, right } = FACE_GUIDE_EYES;
  return {
    x: (offsetX + ((left.x + right.x) / 2) * scale) / paneWidth,
    y: (offsetY + ((left.y + right.y) / 2) * scale) / paneHeight,
    iod: (Math.hypot(right.x - left.x, right.y - left.y) * scale) / paneWidth,
    angle: 0,
  };
}

// Re-express a live pose (fractions of the video frame, mirror already
// applied) in the same pane space as faceGuideTargetPose: the video is laid
// out object-contain in the pane, then scaled about its centre by `cssZoom`
// (the CSS fallback zoom; 1 under hardware zoom, whose frames arrive cropped).
export function poseInPane(
  pose: FacePose,
  video: { width: number; height: number },
  pane: { width: number; height: number },
  cssZoom: number,
): FacePose {
  const fit = Math.min(pane.width / video.width, pane.height / video.height);
  const shownWidth = video.width * fit * cssZoom;
  const shownHeight = video.height * fit * cssZoom;
  const radians = (pose.angle * Math.PI) / 180;
  const eyeDx = pose.iod * Math.cos(radians) * shownWidth;
  const eyeDy = pose.iod * Math.sin(radians) * shownHeight;
  // Mirroring (front camera) swaps which eye comes first, so a level face reads
  // ~180°. An eye line has no direction, so fold it into [-90°, 90°) to compare
  // against the guide's 0°.
  const angle = (Math.atan2(eyeDy, eyeDx) * 180) / Math.PI;
  return {
    x: (pane.width / 2 + (pose.x - 0.5) * shownWidth) / pane.width,
    y: (pane.height / 2 + (pose.y - 0.5) * shownHeight) / pane.height,
    iod: Math.hypot(eyeDx, eyeDy) / pane.width,
    angle: ((angle + 270) % 180) - 90,
  };
}
